/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { find } from 'lodash';

import { getCustomAttributeGqlTypeName } from '@/graphql/utils/custom_attributes/typeNames';
import { parseQuery } from '@/utils/query_parser';

import { composeOldFilterSyntax } from './filters/legacyFiltersUtils';

// TODO: TECHDEBT make sure listing components use gql 'name' format for attributes,
//       instead of currently using both 'name' and 'backendKey'
// TODO: remove columnsMeta after since it won't be needed
/**
 * @param  columns  columns in backendKey representation
 * @return columns  for regular and mapped columns: key in GraphQL key representation,
 *                  for custom attributes: object with GraphQL key representation for 'name'
 *                                         and GraphQL type name for 'type'
 */
export function composeColumnQuery(columns: string[], columnsMeta, attributeMeta): Array<string | object> {
  return columns.map(c => {
    const columnName: string = attributeMeta[c].name;
    const columnConfig = find(columnsMeta.attrs, attr => attr.name == columnName);

    if (columnConfig.isCustomAttribute) {
      // Specify type for custom attribute
      const type: string = getCustomAttributeGqlTypeName(columnConfig.customAttributeType, columnName);
      return { name: columnName, type };
    }

    return columnName;
  });
}

export function composeOrderQuery(sort: SortBy, attributeMeta): object {
  // TODO: change gql order param to accept 'name' format for attribute
  const attrMeta = find(attributeMeta, a => a.name == sort.by);

  return { by: attrMeta.backendKey, direction: sort.direction };
}

export function composeFilterQuery(filters: Filter[], attributeMetaByName): string {
  const filtersSyntax: string = composeOldFilterSyntax(filters, attributeMetaByName);
  const parsedFiltersSyntax = parseQuery(filtersSyntax);
  return JSON.stringify(parsedFiltersSyntax.expression);
}

/**
 * Creates a filter for filtering by ids
 *
 * @param  ids  Object ids [1,2]
 *
 * Example :
 * {
 *  left: 'id',
 *  op: { name: in_array },
 *  right: [1,2],
 * }
 *
 **/
export function composeFilterByIdsQuery(ids: string[]): object {
  return {
    left: 'id',
    op: { name: 'in_array' },
    right: ids,
  };
}

/**
 * Converts a list of Columns to a list of backend field names
 *
 * @param  columns  Columns in `name` format
 * @param  attributeMetaByName  A map from `name` to `backendKey` as returned by
 *                              objectType.attrs GraphQL query
 * @return  fields  An array of `backendKey` strings
 */
export function columnsToFields(columns: Column[], attributeMetaByName): string[] {
  return columns.map(column => {
    return attributeMetaByName[column].backendKey;
  });
}
